import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Badge,
  Field,
  Note,
  Panel,
  ShieldIcon,
  Spinner,
  TableWrap,
} from '@wyocspay/ui';
import { badgeLabel, dateTime, relative } from './format.js';
import {
  ApiError,
  api,
  type AdminCertificate,
  type CertificatePage,
  type IssuedCertificate,
  type ValidityClass,
} from './api.js';

/**
 * Client certificates.
 *
 * This is how somebody gets into this console at all. A certificate is issued
 * here, installed in the operating system keychain of one machine, and presented
 * on the TLS connection before a byte of the application is served.
 *
 * Three things about this page are load-bearing rather than decorative.
 *
 * The passphrase is shown exactly once and is stored nowhere, so the screen that
 * shows it has to say so loudly enough that nobody closes the panel first.
 *
 * Several active certificates for one person is the normal state - one for each
 * machine they work from - so nothing here treats a second one as a mistake.
 *
 * And the page has to be honest in the states before anything works. On a fresh
 * server there is no CA and no certificate, and the useless version of this
 * screen is an empty table under a form that fails when submitted. Each of those
 * states explains itself and says what to do next instead.
 */

/** Just the day. A certificate's expiry is a date, not a moment. */
function dateOnly(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('bg-BG', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, 5000);
    return () => clearInterval(id);
  }, [run]);

  return { data, error, loading, reload: run };
}

export function CertificatesView() {
  const { data, error, loading, reload } = useLoad<CertificatePage>(() => api.certificates());
  const [issued, setIssued] = useState<IssuedCertificate | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  if (loading) return <Spinner label="Зареждане…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  if (!data) return null;

  /**
   * No certificate authority yet.
   *
   * Nothing on this page can work, so nothing else on it is shown. A form whose
   * button is greyed out invites somebody to hunt for what re-enables it; this
   * says what is missing and prints the command that fixes it.
   */
  if (!data.ca.present) {
    return <NoCertificateAuthority page={data} onRecheck={reload} />;
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <CertificateAuthorityPanel page={data} />
      <ModeNote page={data} />

      {issued ? (
        <IssuedPanel issued={issued} onClose={() => setIssued(null)} />
      ) : (
        <IssueForm
          page={data}
          onIssued={async (result) => {
            setIssued(result);
            setMsg(null);
            await reload();
          }}
        />
      )}

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      {data.data.length === 0 ? (
        <FirstCertificateGuidance />
      ) : (
        <CertificateTable
          rows={data.data}
          onRevoked={async (text) => {
            setMsg({ tone: 'ok', text });
            await reload();
          }}
          onError={(text) => setMsg({ tone: 'danger', text })}
        />
      )}

      <InstallationHelp />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The states before anything works
// ---------------------------------------------------------------------------

function NoCertificateAuthority({ page, onRecheck }: { page: CertificatePage; onRecheck: () => void }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note tone="warn" title="Още няма сертифициращ орган">
        Сертификати не могат да се издават, докато на сървъра не бъде създаден CA. Това се прави
        веднъж и след това не се повтаря.
      </Note>

      <Panel title="Създаване на сертифициращ орган">
        <p style={{ marginTop: 0, fontSize: 14 }}>Изпълнете на сървъра, с права на root:</p>
        <pre
          style={{
            background: 'var(--wc-surface-alt)',
            border: '1px solid var(--wc-border)',
            padding: '12px 14px',
            margin: '0 0 16px',
            overflowX: 'auto',
            fontSize: 13,
          }}
        >
          sudo /opt/wyocspay/deploy/create-admin-ca.sh
        </pre>

        <p style={{ fontSize: 13.5 }}>
          Скриптът записва <code>ca.crt</code> и <code>ca.key</code> в{' '}
          <span className="wc-num">{page.ca.directory}</span>, с права 0600 върху ключа. Може да се
          пуска повторно без риск - никога не подменя съществуващ ключ. Направете офлайн копие на{' '}
          <code>ca.key</code>, преди да продължите: без него всеки издаден сертификат става безполезен.
        </p>

        <p style={{ fontSize: 13.5 }}>
          След това издайте първия сертификат от същата обвивка -{' '}
          <code>npm run admin:cert -- --email … --machine …</code> - инсталирайте го, изпробвайте го в
          браузър и чак тогава вдигайте <code>ssl_verify_client</code>. Пълният ред е в{' '}
          <code>deploy/mtls-runbook.md</code>. Обърнат ред заключва всички отвън.
        </p>

        <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onRecheck}>
          Провери отново
        </button>
      </Panel>
    </div>
  );
}

/**
 * The CA is there but nothing has been issued from it.
 *
 * The expected state on a new server, and the one the owner is looking at. An
 * empty table says nothing; this says the next step and the trap after it.
 */
function FirstCertificateGuidance() {
  return (
    <Panel title="Още няма издадени сертификати">
      <p style={{ marginTop: 0, fontSize: 14 }}>
        Сертифициращият орган е готов, но от него още не е издаден нито един сертификат. Това е
        нормално за нов сървър.
      </p>
      <p style={{ fontSize: 13.5, marginBottom: 0 }}>
        Издайте първия от формата по-горе, изтеглете файла, запишете паролата и го инсталирайте в
        ключодържателя на машината. Отворете администрацията отново и се уверете, че браузърът
        предлага сертификата. Едва след като това е потвърдено, се вдига{' '}
        <code>ssl_verify_client</code> в nginx.
      </p>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/** What the CA is, so somebody can confirm at a glance that it is the right one. */
function CertificateAuthorityPanel({ page }: { page: CertificatePage }) {
  return (
    <Panel title="Сертифициращ орган">
      <dl style={{ margin: 0, display: 'grid', gap: 8 }}>
        <Row k="Издател" v={page.ca.subject ?? 'неизвестен'} />
        <Row k="Валиден до" v={dateOnly(page.ca.expires_at)} />
        <Row
          k="Директория"
          v={<span className="wc-num" style={{ fontSize: 12.5 }}>{page.ca.directory}</span>}
        />
        <Row
          k="Права върху ключа"
          v={
            page.ca.key_permissions_ok === false ? (
              <Badge tone="fail">ЧЕТИМ ОТ ДРУГИ</Badge>
            ) : (
              <Badge tone="ok">САМО СОБСТВЕНИКЪТ</Badge>
            )
          }
        />
      </dl>

      {page.ca.key_permissions_ok === false ? (
        <div style={{ marginTop: 14 }}>
          <Note tone="danger" title="Частният ключ на CA е четим от други акаунти">
            {page.ca.directory}/ca.key трябва да е 0600 и собственост на потребителя, под който върви
            услугата. Който има този ключ, може да издаде сертификат за когото и да е администратор.
          </Note>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Where the platform currently stands, in one paragraph.
 *
 * The three states are genuinely different and the difference matters more than
 * anything else on this page: in `off` a certificate is decoration, in
 * `optional` it works but nothing depends on it, in `required` it is the door.
 */
function ModeNote({ page }: { page: CertificatePage }) {
  const active = page.data.filter((c) => c.status === 'ACTIVE' && !c.expired).length;

  if (page.mtls_mode === 'off') {
    return (
      <Note tone="warn" title="Проверката на сертификати е изключена">
        Издадените тук сертификати са валидни, но все още не са задължителни. Първо инсталирайте един
        и го изпробвайте в браузър, след това вдигнете <code>ssl_verify_client optional</code> в nginx
        и <code>ADMIN_MTLS_MODE=optional</code>, и чак накрая <code>required</code>. Редът е описан в
        deploy/mtls-runbook.md - обърнат ред заключва всички отвън.
      </Note>
    );
  }

  if (page.mtls_mode === 'optional') {
    return (
      <Note tone="warn" title="Проверката е включена, но още не е задължителна">
        Представен сертификат се проверява и невалиден се отказва; връзка без сертификат все още
        минава. Това е състоянието за изпробване. Активни сертификати в момента: {active}. Преминете
        към <code>required</code> едва след като сте влезли със сертификат от браузъра, който ползвате.
      </Note>
    );
  }

  return (
    <Note tone="ok" title="Изисква се клиентски сертификат">
      Достъп до администрацията има само машина с валиден сертификат. Активни в момента: {active}.
      Отнемането е незабавно - базата решава, не списък за отмяна.
    </Note>
  );
}

// ---------------------------------------------------------------------------

function IssueForm({
  page,
  onIssued,
}: {
  page: CertificatePage;
  onIssued: (issued: IssuedCertificate) => void | Promise<void>;
}) {
  const [staff, setStaff] = useState<Array<{ id: string; name: string; email: string; role: string }>>([]);
  const [userId, setUserId] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [machine, setMachine] = useState('');
  const [validityClass, setValidityClass] = useState<ValidityClass>('STANDARD');
  const [days, setDays] = useState(String(page.default_validity_days));
  const [note, setNote] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = await api.staff();
        setStaff(
          result.data
            .filter((s) => s.role === 'ADMIN' || s.role === 'SUPER_ADMIN')
            .filter((s) => s.status === 'ACTIVE'),
        );
      } catch {
        // The list is a convenience. A failure here must not block the form.
      }
    })();
  }, []);

  /**
   * Choosing the account fills in the name and the email.
   *
   * The email is not merely a default: the server refuses a certificate whose
   * email is not the account's own, because a certificate that names a
   * different account can never be used to sign in. Filling it in is how that
   * rule stops being a surprise at the last step.
   */
  function pick(id: string): void {
    setUserId(id);
    const member = staff.find((s) => s.id === id);
    if (member) {
      setFullName(member.name);
      setEmail(member.email);
    }
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.issueCertificate({
        user_id: userId,
        full_name: fullName.trim(),
        email: email.trim(),
        machine_label: machine.trim(),
        validity_class: validityClass,
        ...(validityClass === 'STANDARD' ? { validity_days: Number(days) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(passphrase.trim() ? { passphrase: passphrase.trim() } : {}),
      });
      setMachine('');
      setNote('');
      setPassphrase('');
      setValidityClass('STANDARD');
      await onIssued(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Издай сертификат">
      <form onSubmit={submit}>
        <Field label="Администратор" hint="Сертификатът важи само за този акаунт.">
          <select className="wc-input" value={userId} onChange={(e) => pick(e.target.value)} required>
            <option value="">Изберете акаунт…</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.email} · {badgeLabel(s.role)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Пълно име" hint="Показва се в диалога за избор на сертификат в браузъра.">
          <input className="wc-input" value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={64} required />
        </Field>

        <Field label="Имейл" hint="Трябва да съвпада с имейла на акаунта, иначе входът се отказва.">
          <input className="wc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={128} required />
        </Field>

        <Field
          label="Машина"
          hint="Например „MacBook Pro“ или „Компютър в офиса“. По това ще познаете кой сертификат да отнемете, ако машината се загуби."
        >
          <input className="wc-input" value={machine} onChange={(e) => setMachine(e.target.value)} maxLength={64} required />
        </Field>

        <ValidityChoice
          page={page}
          value={validityClass}
          onChange={setValidityClass}
          days={days}
          onDaysChange={setDays}
          note={note}
          onNoteChange={setNote}
        />

        <Field
          label="Парола за сертификата (по избор)"
          hint="Вие избирате паролата за .p12 файла - може да е каквато поискате, поне 12 знака (напр. кратка фраза от няколко думи). Оставите ли полето празно, системата ще генерира силна парола и ще я покаже само веднъж."
        >
          <input
            className="wc-input"
            type="text"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="напр. няколко-думи-които-помните"
            minLength={12}
            maxLength={128}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        {error ? <div style={{ marginBottom: 14 }}><Note tone="danger">{error}</Note></div> : null}

        <button type="submit" className="wc-btn" disabled={busy}>
          <ShieldIcon size={14} /> {busy ? 'Издаване…' : 'Издай сертификат'}
        </button>

        <p style={{ fontSize: 12.5, color: 'var(--wc-muted)', margin: '12px 0 0' }}>
          Един администратор може да има няколко активни сертификата - по един за всяка машина. Това е
          нормално, а не грешка.
        </p>
      </form>
    </Panel>
  );
}

/**
 * Standard or long-lived, chosen explicitly.
 *
 * Two named options rather than a day count that happens to accept a large
 * number: a ten-year certificate should be something somebody decided to issue,
 * not something they could end up with by typing 3650 into a box.
 *
 * The long option shows the real date it would produce. There is no such thing
 * as a certificate that does not expire, and a label implying otherwise would be
 * a lie that surfaces on one specific morning years from now.
 */
function ValidityChoice({
  page,
  value,
  onChange,
  days,
  onDaysChange,
  note,
  onNoteChange,
}: {
  page: CertificatePage;
  value: ValidityClass;
  onChange: (value: ValidityClass) => void;
  days: string;
  onDaysChange: (value: string) => void;
  note: string;
  onNoteChange: (value: string) => void;
}) {
  const caDate = dateOnly(page.ca.expires_at);
  const longLivedDays = page.ca.long_lived_days;

  return (
    <div style={{ marginBottom: 16 }}>
      <span className="wc-label" style={{ display: 'block', marginBottom: 6 }}>
        Срок
      </span>

      <div style={{ display: 'grid', gap: 8 }}>
        <label
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '11px 13px',
            border: `1px solid ${value === 'STANDARD' ? 'var(--wc-blue)' : 'var(--wc-border)'}`,
            background: value === 'STANDARD' ? 'var(--wc-blue-tint)' : 'var(--wc-surface)',
            cursor: 'pointer',
          }}
        >
          <input
            type="radio"
            name="validity-class"
            checked={value === 'STANDARD'}
            onChange={() => onChange('STANDARD')}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong style={{ fontSize: 14 }}>Стандартен</strong>
            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--wc-muted)', marginTop: 2 }}>
              Изтича след избран брой дни. Това е правилният избор, освен ако няма конкретна причина
              за друго.
            </span>
          </span>
        </label>

        <label
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '11px 13px',
            border: `1px solid ${value === 'LONG_LIVED' ? 'var(--wc-amber)' : 'var(--wc-border)'}`,
            background: value === 'LONG_LIVED' ? 'var(--wc-amber-tint)' : 'var(--wc-surface)',
            cursor: 'pointer',
          }}
        >
          <input
            type="radio"
            name="validity-class"
            checked={value === 'LONG_LIVED'}
            onChange={() => onChange('LONG_LIVED')}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong style={{ fontSize: 14 }}>За целия живот на сертифициращия орган</strong>
            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--wc-muted)', marginTop: 2 }}>
              Валиден до <strong>{caDate}</strong>
              {longLivedDays !== null ? ` (около ${longLivedDays} дни)` : ''} - датата, на която
              изтича самият CA. Безсрочни сертификати няма; сертификат, по-дълъг от издателя си, просто
              спира да работи в деня, в който CA изтече.
            </span>
          </span>
        </label>
      </div>

      {value === 'STANDARD' ? (
        <div style={{ marginTop: 12 }}>
          <Field label="Валидност (дни)" hint={`От ${page.min_validity_days} до ${page.max_validity_days}.`}>
            <input
              className="wc-input wc-num"
              type="number"
              value={days}
              onChange={(e) => onDaysChange(e.target.value)}
              min={page.min_validity_days}
              max={page.max_validity_days}
              required
            />
          </Field>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {/*
            * The trade-off, stated where the choice is made.
            *
            * A long-lived certificate is normally poor practice, and the reason
            * it is defensible here is specific and fragile: revocation on this
            * platform is a database check, not a CRL. Whoever later moves
            * revocation to a CRL needs to know this option depended on that.
            */}
          <Note tone="warn" title="Защо това е допустимо тук">
            Дългосрочните сертификати обикновено са лоша практика: отнемането им зависи от списък за
            отмяна (CRL), който се разпространява бавно, кешира се и може да се провали безшумно - и
            тогава отнет сертификат продължава да работи. Тук това не важи. Отнемането е проверка в
            базата при всяка заявка, влиза в сила незабавно и прекратява всички сесии на този
            сертификат в същата операция.
            <div style={{ marginTop: 6 }}>
              <strong>
                Ако някога отнемането мине през CRL или OCSP, тази опция трябва да отпадне заедно с
                него.
              </strong>
            </div>
          </Note>

          <div style={{ marginTop: 12 }}>
            <Field
              label="Причина (задължителна)"
              hint="Записва се към сертификата. След година трябва да е ясно защо съществува."
            >
              <textarea
                className="wc-input"
                value={note}
                onChange={(e) => onNoteChange(e.target.value)}
                rows={3}
                minLength={10}
                maxLength={500}
                required
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The bundle and the passphrase, once.
 *
 * Deliberately not dismissible by accident: closing this panel is the only way
 * off it, and the button says what closing means. The passphrase exists in this
 * response and in no database, no log and no audit entry.
 */
function IssuedPanel({ issued, onClose }: { issued: IssuedCertificate; onClose: () => void }) {
  const [downloaded, setDownloaded] = useState(false);

  function download(): void {
    const bytes = Uint8Array.from(atob(issued.pkcs12_base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/x-pkcs12' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = issued.filename;
    anchor.click();
    URL.revokeObjectURL(url);
    setDownloaded(true);
  }

  return (
    <Panel title="Сертификатът е издаден">
      {issued.passphrase_chosen ? (
        <Note tone="ok" title="Паролата е тази, която въведохте">
          Файлът е защитен с паролата, която зададохте при издаването. Тя не се показва и не се
          съхранява тук - използвайте я при импортиране на .p12 файла в ключодържателя.
        </Note>
      ) : (
        <>
          <Note tone="danger" title="Паролата се показва само сега">
            Тя не се съхранява никъде и не може да бъде възстановена. Запишете я, преди да затворите този
            панел. Ако я загубите, отнемете сертификата и издайте нов - отнема минута.
          </Note>

          <div
            style={{
              margin: '16px 0',
              padding: '14px 16px',
              background: 'var(--wc-surface-alt)',
              border: '1px solid var(--wc-border)',
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--wc-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Парола за импортиране
            </div>
            <div className="wc-num" style={{ fontSize: 20, letterSpacing: '0.06em', marginTop: 6, userSelect: 'all' }}>
              {issued.passphrase}
            </div>
          </div>
        </>
      )}

      <dl style={{ margin: '0 0 16px', display: 'grid', gap: 8 }}>
        <Row k="Издаден на" v={`${issued.common_name} · ${issued.email}`} />
        <Row k="Машина" v={issued.machine_label} />
        <Row
          k="Срок"
          v={
            issued.validity_class === 'LONG_LIVED'
              ? `до изтичане на CA - ${dateOnly(issued.expires_at)}`
              : `стандартен - до ${dateOnly(issued.expires_at)}`
          }
        />
        <Row
          k="Отпечатък SHA-256"
          v={<span className="wc-num" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{issued.fingerprint_sha256}</span>}
        />
      </dl>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn" onClick={download}>
          Изтегли {issued.filename}
        </button>
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onClose}>
          {downloaded ? 'Готово, скрий паролата' : 'Затвори без изтегляне'}
        </button>
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--wc-muted)', margin: '14px 0 0' }}>
        Прехвърлете файла до машината по канал, на който имате доверие. Не го изпращайте по имейл.
        Изтрийте го, след като бъде импортиран - файлът е удостоверение.
      </p>
    </Panel>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'baseline' }}>
      <dt style={{ fontSize: 13, color: 'var(--wc-muted)', flex: 'none' }}>{k}</dt>
      <dd style={{ margin: 0, fontSize: 13.5, color: 'var(--wc-ink)', textAlign: 'right', minWidth: 0 }}>{v}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CertificateTable({
  rows,
  onRevoked,
  onError,
}: {
  rows: AdminCertificate[];
  onRevoked: (text: string) => void | Promise<void>;
  onError: (text: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function revoke(certificate: AdminCertificate): Promise<void> {
    const reason = window.prompt(
      `Отнемане на сертификата на ${certificate.email} за „${certificate.machine_label}“.\n\nПричина (записва се в одитния дневник):`,
    );
    if (!reason || reason.trim().length < 3) return;

    setBusy(certificate.id);
    try {
      const result = await api.revokeCertificate(certificate.id, reason.trim());
      await onRevoked(`Сертификатът е отнет. Незабавно прекратени сесии: ${result.sessions_terminated}.`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel title="Издадени сертификати" padded={false}>
      <TableWrap>
        <table className="wc-table">
          <thead>
            <tr>
              <th>Притежател</th>
              <th>Машина</th>
              <th>Отпечатък SHA-256</th>
              <th>Издаден</th>
              <th>Валиден до</th>
              <th>Статус</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.common_name}</strong>
                  <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{c.email}</div>
                </td>
                <td style={{ fontSize: 13 }}>
                  {c.machine_label}
                  {c.validity_class === 'LONG_LIVED' ? (
                    <div style={{ marginTop: 3 }}>
                      <Badge tone="warn" icon={false}>ДО ИЗТИЧАНЕ НА CA</Badge>
                      {c.issuance_note ? (
                        <div style={{ fontSize: 11.5, color: 'var(--wc-muted)', marginTop: 2, maxWidth: 220 }}>
                          {c.issuance_note}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </td>
                <td className="wc-num" style={{ fontSize: 11, maxWidth: 240, wordBreak: 'break-all' }}>
                  {c.fingerprint_sha256}
                </td>
                <td style={{ fontSize: 13, color: 'var(--wc-muted)', whiteSpace: 'nowrap' }}>
                  {dateTime(c.issued_at)}
                </td>
                <td style={{ fontSize: 13, color: 'var(--wc-muted)', whiteSpace: 'nowrap' }}>
                  {dateOnly(c.expires_at)}
                  {c.status === 'ACTIVE' && !c.expired && c.days_remaining <= 30 ? (
                    <div style={{ fontSize: 12, color: 'var(--wc-amber)' }}>
                      остават {c.days_remaining} дни
                    </div>
                  ) : null}
                </td>
                <td>
                  {c.status === 'REVOKED' ? (
                    <>
                      <Badge tone="fail">ОТНЕТ</Badge>
                      {c.revoked_reason ? (
                        <div style={{ fontSize: 11.5, color: 'var(--wc-muted)' }}>{c.revoked_reason}</div>
                      ) : null}
                      {c.revoked_at ? (
                        <div style={{ fontSize: 11.5, color: 'var(--wc-muted)' }}>{relative(c.revoked_at)}</div>
                      ) : null}
                    </>
                  ) : c.expired ? (
                    <Badge tone="warn">ИЗТЕКЪЛ</Badge>
                  ) : (
                    <Badge tone="ok">АКТИВЕН</Badge>
                  )}
                </td>
                <td className="wc-right">
                  {c.status === 'ACTIVE' ? (
                    <button
                      type="button"
                      className="wc-btn wc-btn-sm wc-btn-danger"
                      disabled={busy === c.id}
                      onClick={() => revoke(c)}
                    >
                      Отнеми
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function InstallationHelp() {
  return (
    <Panel title="Инсталиране на сертификата">
      <div style={{ display: 'grid', gap: 16, fontSize: 13.5 }}>
        <div>
          <h3 style={{ fontSize: 14, margin: '0 0 6px', color: 'var(--wc-blue-ink)' }}>macOS</h3>
          <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
            <li>Щракнете двукратно върху файла <code>.p12</code>. Отваря се Keychain Access.</li>
            <li>Изберете ключодържателя <strong>login</strong> и въведете паролата за импортиране.</li>
            <li>Отворете {location.host} в Safari или Chrome. Браузърът пита кой сертификат да използва - изберете този на Wyoming Cloud Solutions LLC.</li>
            <li>Изтрийте файла <code>.p12</code> след импортирането.</li>
          </ol>
        </div>

        <div>
          <h3 style={{ fontSize: 14, margin: '0 0 6px', color: 'var(--wc-blue-ink)' }}>Windows</h3>
          <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
            <li>Щракнете двукратно върху файла <code>.p12</code>. Стартира Certificate Import Wizard.</li>
            <li>Изберете <strong>Current User</strong>, не Local Machine.</li>
            <li>Въведете паролата за импортиране. Оставете „Automatically select the certificate store“.</li>
            <li>Отворете {location.host} в Edge или Chrome и изберете сертификата, когато бъдете попитани.</li>
            <li>Изтрийте файла <code>.p12</code> след импортирането.</li>
          </ol>
        </div>

        <Note>
          Изборът на сертификат се помни за цялата сесия на браузъра. Ако сте избрали грешен,
          затворете всички прозорци на браузъра и отворете адреса отново.
        </Note>
      </div>
    </Panel>
  );
}
